import { useState, useRef, useEffect, DragEvent } from "react";
import { Upload, X, CheckCircle, AlertCircle, File, Image, Video } from "lucide-react";
import { useMediaUploadWithQuota } from "../hooks/useMediaUploadWithQuota";
import ImageCropper from "./ImageCropper";
import {
  MediaType,
  ALLOWED_VIDEO_TYPES,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_FILE_TYPES,
  ALLOWED_VIDEO_EXTENSIONS,
  ALLOWED_IMAGE_EXTENSIONS,
  ALLOWED_FILE_EXTENSIONS,
  MAX_FILE_SIZE,
} from "../types/media";
import { optimizeImageByType, isImageFile } from "../utils/imageOptimization";
import { checkAndConfirmDuplicate, duplicateDetection } from "../utils/duplicateDetection";

interface MediaUploadProps {
  courseId: string;
  mediaType: MediaType;
  onUploadComplete?: (mediaId: string, url: string, jobId?: string) => void;
  onUploadError?: (error: string) => void;
  compact?: boolean;
  aspectRatio?: number; // For image cropping (width/height)
}

export default function MediaUpload({
  courseId,
  mediaType,
  onUploadComplete,
  onUploadError,
  compact = false,
  aspectRatio = 16 / 9,
}: MediaUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadMedia, uploading, progress } = useMediaUploadWithQuota();

  console.log('[MediaUpload] Rendered with:', { 
    courseId, 
    mediaType, 
    hasCallback: !!onUploadComplete,
    uploading,
    hasProgress: !!progress,
    progressStatus: progress?.status
  });

  const getAllowedTypes = () => {
    switch (mediaType) {
      case "video":
        return ALLOWED_VIDEO_TYPES;
      case "image":
        return ALLOWED_IMAGE_TYPES;
      case "file":
        return ALLOWED_FILE_TYPES;
    }
  };

  const getAllowedExtensions = () => {
    switch (mediaType) {
      case "video":
        return ALLOWED_VIDEO_EXTENSIONS;
      case "image":
        return ALLOWED_IMAGE_EXTENSIONS;
      case "file":
        return ALLOWED_FILE_EXTENSIONS;
    }
  };

  const getMediaIcon = () => {
    switch (mediaType) {
      case "video":
        return <Video className="w-12 h-12" />;
      case "image":
        return <Image className="w-12 h-12" />;
      case "file":
        return <File className="w-12 h-12" />;
    }
  };

  const getMediaLabel = () => {
    switch (mediaType) {
      case "video":
        return "วิดีโอ";
      case "image":
        return "รูปภาพ";
      case "file":
        return "ไฟล์ประกอบการสอน";
    }
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = getAllowedTypes();
    const allowedExtensions = getAllowedExtensions();
    const maxSize = MAX_FILE_SIZE[mediaType];

    if (!allowedTypes.includes(file.type)) {
      return `ประเภทไฟล์ไม่ถูกต้อง กรุณาเลือกไฟล์ประเภท: ${allowedExtensions.join(", ")}`;
    }

    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      return `นามสกุลไฟล์ไม่ถูกต้อง กรุณาเลือกไฟล์: ${allowedExtensions.join(", ")}`;
    }

    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      return `ไฟล์มีขนาดใหญ่เกินไป (สูงสุด ${maxSizeMB}MB)`;
    }

    return null;
  };

  const handleFileSelect = async (file: File) => {
    const error = validateFile(file);
    if (error) {
      if (onUploadError) onUploadError(error);
      return;
    }

    // Check for duplicate files
    const duplicateCheck = await checkAndConfirmDuplicate(file);
    if (!duplicateCheck.shouldUpload && duplicateCheck.existingUrl) {
      console.log('[MediaUpload] Using existing file:', duplicateCheck.existingUrl);
      if (onUploadComplete) {
        onUploadComplete('existing', duplicateCheck.existingUrl);
      }
      return;
    }

    // Optimize image before upload
    let fileToUpload = file;
    if (isImageFile(file) && mediaType === "image") {
      try {
        console.log('[MediaUpload] Optimizing image...');
        const imageType = aspectRatio === 1 ? 'profile' : 
                         aspectRatio === 16/5 ? 'cover' : 
                         aspectRatio === 16/9 ? 'thumbnail' : 
                         aspectRatio === 4/5 ? 'intro' : 'general';
        fileToUpload = await optimizeImageByType(file, imageType);
      } catch (error) {
        console.error('[MediaUpload] Image optimization failed, using original:', error);
      }
    }
    
    setSelectedFile(fileToUpload);
    
    // For images, show cropper first
    if (mediaType === "image") {
      setShowCropper(true);
      return;
    }
    
    // For videos and files, upload immediately
    try {
      console.log("Auto-uploading file:", fileToUpload.name);
      const result = await uploadMedia(courseId, fileToUpload, mediaType);
      console.log("Upload result:", result);
      
      // Register uploaded file for duplicate detection
      await duplicateDetection.registerUpload(file, result.url);
      
      if (onUploadComplete) {
        onUploadComplete(result.mediaId, result.url, result.jobId);
      }
      
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (uploadError: any) {
      console.error("Upload error:", uploadError);
      if (onUploadError) {
        onUploadError(uploadError.message || "เกิดข้อผิดพลาดในการอัปโหลด");
      }
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      console.log("handleCropComplete called with blob:", croppedBlob);
      
      if (!croppedBlob || !(croppedBlob instanceof Blob)) {
        console.error("Invalid blob received", croppedBlob);
        if (onUploadError) onUploadError("เกิดข้อผิดพลาด: ไม่สามารถประมวลผลรูปภาพได้");
        setShowCropper(false);
        return;
      }

      const fileName = selectedFile?.name || `cover-${Date.now()}.jpg`;
      console.log("Creating File with name:", fileName, "blob size:", croppedBlob.size);
      
      // Create a File object from blob with proper properties
      const croppedFile = Object.assign(croppedBlob, {
        name: fileName,
        lastModified: Date.now(),
      }) as File;
      
      console.log("File created successfully:", croppedFile);
      setShowCropper(false);
      
      // Auto-upload immediately after cropping
      try {
        console.log("Starting auto-upload after crop...");
        const result = await uploadMedia(courseId, croppedFile, mediaType);
        console.log("Upload result:", result);
        console.log("onUploadComplete exists?", !!onUploadComplete);
        console.log("onUploadComplete type:", typeof onUploadComplete);
        
        // Register uploaded file for duplicate detection
        if (selectedFile) {
          await duplicateDetection.registerUpload(selectedFile, result.url);
        }
        
        if (onUploadComplete) {
          onUploadComplete(result.mediaId, result.url);
        }
        
        // Reset component state to allow parent to show the image
        console.log("Resetting MediaUpload component state...");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        console.log("MediaUpload state reset complete");
      } catch (uploadError: any) {
        console.error("Upload error:", uploadError);
        if (onUploadError) {
          onUploadError(uploadError.message || "เกิดข้อผิดพลาดในการอัปโหลด");
        }
      }
    } catch (error) {
      console.error("Error in handleCropComplete:", error);
      if (onUploadError) onUploadError("เกิดข้อผิดพลาดในการประมวลผลรูปภาพ");
      setShowCropper(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const result = await uploadMedia(courseId, selectedFile, mediaType);
      
      if (onUploadComplete) {
        onUploadComplete(result.mediaId, result.url);
      }
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      if (onUploadError) {
        onUploadError(error.message || "เกิดข้อผิดพลาดในการอัปโหลด");
      }
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="w-full">
      {showCropper && selectedFile && mediaType === "image" && (
        <ImageCropper
          imageFile={selectedFile}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={aspectRatio}
        />
      )}
      {!selectedFile && !uploading && (
        compact ? (
          <div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all inline-flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              เลือกไฟล์
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={getAllowedTypes().join(",")}
              onChange={handleFileInputChange}
              className="hidden"
            />
            <p className="text-xs text-gray-500 mt-2">
              ไฟล์ที่รองรับ: {getAllowedExtensions().join(", ")} • ขนาดสูงสุด: {Math.round(MAX_FILE_SIZE[mediaType] / (1024 * 1024))}MB
            </p>
          </div>
        ) : (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
              isDragging
                ? "border-blue-600 bg-blue-50"
                : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
            }`}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="text-blue-600">{getMediaIcon()}</div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  อัปโหลด{getMediaLabel()}
                </h3>
                <p className="text-gray-600 mb-4">
                  ลากและวางไฟล์ที่นี่ หรือคลิกเพื่อเลือกไฟล์
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  <Upload className="w-5 h-5 inline mr-2" />
                  เลือกไฟล์
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={getAllowedTypes().join(",")}
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>
              <div className="text-sm text-gray-500">
                <p>ไฟล์ที่รองรับ: {getAllowedExtensions().join(", ")}</p>
                <p>ขนาดสูงสุด: {Math.round(MAX_FILE_SIZE[mediaType] / (1024 * 1024))}MB</p>
              </div>
            </div>
          </div>
        )
      )}

      {selectedFile && !uploading && (
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="text-blue-600">{getMediaIcon()}</div>
              <div>
                <p className="font-semibold text-gray-800">{selectedFile.name}</p>
                <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-red-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleUpload}
              className="flex-1 bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              เริ่มอัปโหลด
            </button>
            <button
              onClick={handleCancel}
              className="px-6 py-3 rounded-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
            >
              ยกเลิก
            </button>
          </div>
        </div>
      )}

      {uploading && progress && (
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-blue-600">{getMediaIcon()}</div>
            <div className="flex-1">
              <p className="font-semibold text-gray-800">{progress.fileName}</p>
              <p className="text-sm text-gray-600">
                {progress.status === "uploading" && (progress.message || `กำลังอัปโหลด... ${progress.progress}%`)}
                {progress.status === "success" && mediaType === "video" && "อัปโหลดสำเร็จ ✅ วิดีโอพร้อมใช้งาน"}
                {progress.status === "success" && mediaType !== "video" && "อัปโหลดสำเร็จ!"}
                {progress.status === "error" && `เกิดข้อผิดพลาด: ${progress.error}`}
              </p>
              {progress.status === "uploading" && progress.message && (
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  ⚠️ กรุณาอย่าปิดหน้านี้ระหว่างอัปโหลด
                </p>
              )}
            </div>
            {progress.status === "success" && (
              <CheckCircle className="w-6 h-6 text-green-600" />
            )}
            {progress.status === "error" && (
              <AlertCircle className="w-6 h-6 text-red-600" />
            )}
          </div>

          {progress.status === "uploading" && (
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              {progress.progress === 0 || (progress.progress === 100 && progress.message?.includes('ประมวลผล')) ? (
                // Animated loading bar when preparing/starting or processing after 100%
                <div className="h-3 bg-gradient-to-r from-blue-600 to-pink-600 rounded-full animate-pulse">
                  <div className="h-full w-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              ) : (
                // Normal progress bar
                <div
                  className="bg-gradient-to-r from-blue-600 to-pink-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                ></div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
